import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye, Star, Check, Trash2, QrCode, Settings, Upload } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { BADGE_LAYOUTS } from '../utils/constants';
import { getConsistentPreviewData, getConsistentLogoConfig } from '../utils/badgePreviewData';
import BadgePreview from '../components/badges/BadgePreview';
import TemplatePreviewModal from '../components/badges/TemplatePreviewModal';
import CreateTemplateModal from '../components/badges/CreateTemplateModal';
import LogoConfigurationModal from '../components/badges/LogoConfigurationModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { useToastContext } from '../context/ToastContext';
import { useBadgeSettings } from '../context/BadgeSettingsContext';
import { useEvent } from '../context/EventContext';
import { badgeAPI, eventAPI } from '../api/endpoints';

const BadgeTemplates = () => {
  const toast = useToastContext();
  const queryClient = useQueryClient();
  const {
    defaultTemplate,
    showQR,
    showLogo,
    defaultPrintMode,
    updateDefaultTemplate,
    updateShowQR,
    updateShowLogo,
    updateDefaultPrintMode
  } = useBadgeSettings();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  
  // Confirm delete modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ['events'],
    queryFn: async () => (await eventAPI.getAll()).data,
  });

  // Fetch badge templates
  const { data: templatesData } = useQuery({
    queryKey: ['badge-templates', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return { templates: [] };
      return (await badgeAPI.getTemplates(selectedEvent)).data;
    },
  });

  // Auto-select first event
  React.useEffect(() => {
    const events = eventsData?.events || [];
    if (events.length > 0 && !selectedEvent) {
      setSelectedEvent(events[0].id);
    }
  }, [eventsData, selectedEvent, setSelectedEvent]);

  // Create template mutation
  const createMutation = useMutation({
    mutationFn: (data) => badgeAPI.createTemplate({ ...data, event_id: selectedEvent }),
    onSuccess: () => {
      queryClient.invalidateQueries(['badge-templates']);
      toast.success('Badge template created successfully!');
      setShowCreateModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create template');
    },
  });

  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => badgeAPI.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['badge-templates']);
      toast.success('Template deleted successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete template');
    },
  });

  const handleCreateTemplate = (templateData) => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    createMutation.mutate(templateData);
  };

  const handleDeleteTemplate = (id, name) => {
    setDeleteConfirm({ open: true, id, name });
  };
  
  const confirmDeleteTemplate = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
    setDeleteConfirm({ open: false, id: null, name: '' });
  };

  const customTemplates = templatesData?.data || [];
  const events = eventsData?.events || [];

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  const handleSaveSettings = (settings) => {
    updateShowQR(settings.showQR);
    updateShowLogo(settings.showLogo);
    toast.success('Template settings saved!');
  };

  const handleLogoUpdated = () => {
    // Refresh events data to get updated logo information
    queryClient.invalidateQueries(['events']);
    setShowLogoModal(false);
  };

  const getSelectedEventData = () => {
    const events = eventsData?.events || [];
    return events.find(event => event.id === selectedEvent);
  };


  const handleSetDefault = (templateValue) => {
    updateDefaultTemplate(templateValue);
    toast.success(`Template set as default!`);
  };

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Badge Templates</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Design and manage badge templates</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm w-full sm:w-auto"
            >
              <option value="">All Events</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
            {selectedEvent && (
              <Button
                variant="secondary"
                onClick={() => setShowLogoModal(true)}
                className="gap-2 w-full sm:w-auto"
              >
                <Upload className="h-5 w-5" />
                <span className="hidden xs:inline">Logo Settings</span>
                <span className="xs:hidden">Logo</span>
              </Button>
            )}
            <Button
              onClick={() => setShowCreateModal(true)}
              className="gap-2 w-full sm:w-auto"
              disabled={!selectedEvent}
            >
              <Plus className="h-5 w-5" />
              <span className="hidden xs:inline">Create Template</span>
              <span className="xs:hidden">Create</span>
            </Button>
          </div>
        </div>

        {/* Logo Status Indicator */}
        {selectedEvent && (
          <Card>
            <Card.Content>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-gray-500" />
                    <span className="font-medium text-gray-900 text-sm sm:text-base">Event Logo Status</span>
                  </div>
                  {getSelectedEventData()?.logo_url ? (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex items-center gap-2">
                        <img
                          src={getSelectedEventData().logo_url}
                          alt="Event Logo"
                          className="h-8 w-8 object-cover rounded-full shadow-sm"
                        />
                        <span className="text-sm text-green-600 font-medium">Logo Uploaded</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        Position: {getSelectedEventData()?.logo_position || 'top-center'} •
                        Shape: {getSelectedEventData()?.logo_shape || 'circular'} •
                        Size: {getSelectedEventData()?.logo_size || 60}px
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">No logo uploaded</span>
                  )}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowLogoModal(true)}
                  className="gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Configure
                </Button>
              </div>
            </Card.Content>
          </Card>
        )}

        {/* Custom Templates */}
        {customTemplates.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Custom Templates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {customTemplates.map((template) => (
                <Card key={template.id}>
                  <Card.Content>
                    <div className="space-y-4">
                      {/* Template Preview */}
                      <div className="aspect-[3/4] bg-gray-50 rounded-lg overflow-hidden border-2 border-gray-200">
                        <div
                          className="w-full h-full relative"
                          style={{ backgroundColor: template.design_config?.backgroundColor || '#FFFFFF' }}
                        >
                          {/* Header */}
                          <div
                            className="w-full flex items-center justify-center relative"
                            style={{
                              backgroundColor: template.design_config?.primaryColor || '#3B82F6',
                              height: `${template.design_config?.headerHeight || 50}px`,
                            }}
                          >
                            <p className="text-white text-xs font-bold tracking-wide uppercase">
                              {template.event_name || 'Event Name'}
                            </p>
                          </div>

                          {/* Main Content */}
                          <div className="px-4 py-6 flex flex-col items-center justify-center h-full">
                            {/* Name - Only show once */}
                            <p
                              className="font-bold text-center mb-2"
                              style={{
                                color: template.design_config?.secondaryColor || '#1F2937',
                                fontSize: `${Math.min(template.design_config?.nameSize || 14, 14)}px`,
                              }}
                            >
                              Sample Name
                            </p>

                            {/* Title */}
                            <p className="text-xs text-center text-gray-600 mb-1">
                              Job Title
                            </p>

                            {/* Company */}
                            <p className="text-xs text-center text-gray-500 mb-3">
                              Company Name
                            </p>

                            {/* QR Placeholder */}
                            {template.design_config?.showQRCode && (
                              <div className="bg-white p-2 rounded shadow-sm">
                                <div
                                  className="bg-gray-300 rounded flex items-center justify-center"
                                  style={{
                                    width: `${Math.min(template.design_config?.qrSize || 60, 60)}px`,
                                    height: `${Math.min(template.design_config?.qrSize || 60, 60)}px`,
                                  }}
                                >
                                  <QrCode className="h-8 w-8 text-gray-600" />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Template Info */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{template.template_name}</h3>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            {template.template_type}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.id, template.template_name)}
                          className="flex-1 gap-2 text-xs sm:text-sm"
                          loading={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="hidden xs:inline">Delete</span>
                          <span className="xs:hidden">Del</span>
                        </Button>
                      </div>
                    </div>
                  </Card.Content>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Default Templates */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Default Templates</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {BADGE_LAYOUTS.map((template) => (
              <Card key={template.value}>
                <Card.Content>
                  <div className="space-y-4">
                    {/* Template Preview */}
                    <div className="aspect-[3/4] bg-gray-50 rounded-lg overflow-hidden border-2 border-gray-200">
                      <BadgePreview
                        layout={template.value}
                        showQR={showQR}
                        showLogo={showLogo}
                        data={getConsistentPreviewData(getSelectedEventData()?.name)}
                        logoConfig={getConsistentLogoConfig(getSelectedEventData())}
                      />
                    </div>

                    {/* Template Info */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{template.label}</h3>
                        {defaultTemplate === template.value && (
                          <div className="flex items-center gap-1 text-green-600">
                            <Check className="h-4 w-4" />
                            <span className="text-xs font-medium">Default</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{template.description}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePreview(template)}
                        className="flex-1 gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </Button>
                      <Button
                        variant={defaultTemplate === template.value ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => handleSetDefault(template.value)}
                        className="flex-1 gap-2"
                      >
                        <Star className="h-4 w-4" />
                        {defaultTemplate === template.value ? 'Default' : 'Set Default'}
                      </Button>
                    </div>
                  </div>
                </Card.Content>
              </Card>
            ))}
          </div>

          {/* Template Settings */}
          <Card>
            <Card.Content>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Global Badge Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Template
                  </label>
                  <select
                    value={defaultTemplate}
                    onChange={(e) => handleSetDefault(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {BADGE_LAYOUTS.map((template) => (
                      <option key={template.value} value={template.value}>
                        {template.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">This template will be used by default for all badge printing</p>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      id="show-qr"
                      checked={showQR}
                      onChange={(e) => updateShowQR(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Show QR code on all badges</div>
                      <div className="text-xs text-gray-500">Enable QR codes for quick check-in scanning</div>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      id="show-logo"
                      checked={showLogo}
                      onChange={(e) => updateShowLogo(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Show event logo on badges</div>
                      <div className="text-xs text-gray-500">Display event branding on all printed badges</div>
                    </div>
                  </label>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Default Print Mode</h4>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="printMode"
                        value="sticker"
                        checked={defaultPrintMode === 'sticker'}
                        onChange={(e) => updateDefaultPrintMode(e.target.value)}
                        className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Stickers (Default)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="printMode"
                        value="badge"
                        checked={defaultPrintMode === 'badge'}
                        onChange={(e) => updateDefaultPrintMode(e.target.value)}
                        className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Badges</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Choose what Print Station opens with by default</p>
                </div>
              </div>
            </Card.Content>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      <TemplatePreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        template={previewTemplate}
        onSave={handleSaveSettings}
      />

      {/* Create Template Modal */}
      <CreateTemplateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateTemplate}
        loading={createMutation.isPending}
      />

      {/* Logo Configuration Modal */}
      <LogoConfigurationModal
        isOpen={showLogoModal}
        onClose={() => setShowLogoModal(false)}
        event={getSelectedEventData()}
        onLogoUpdated={handleLogoUpdated}
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={confirmDeleteTemplate}
        title="Delete Template"
        message={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default BadgeTemplates;
